/**
 * Technical inspection and service due reminders (feature "reminders", owner's decision 2026-11-01).
 *
 * The vehicle keeps the inspection's "valid until" date and the next service (date and/or km);
 * the customer gives consent per channel (GDPR: off by default, the time of consent is kept by
 * the database). A scheduled daily job sends each reminder once per vehicle and due date, a set
 * number of days before the date (workshop settings). Dates are plain calendar days "YYYY-MM-DD".
 * Keep the window rule equal to public.reminder_dues() in 20261128000000_reminders.sql.
 */
export type ReminderKind = "inspection" | "service";
export const REMINDER_KINDS: readonly ReminderKind[] = ["inspection", "service"];
export type ReminderChannel = "sms" | "email";
export const REMINDER_CHANNELS: readonly ReminderChannel[] = ["email", "sms"];

/** Days before the due date when the reminder goes out (settings defaults). */
export const REMINDER_DEFAULT_DAYS: Record<ReminderKind, number> = { inspection: 30, service: 14 };
/** The reminders page lists what falls due within this many days. */
export const REMINDER_HORIZON_DAYS = 60;

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar day written as YYYY-MM-DD (years 2000 to 2100). */
export function isIsoDate(s: unknown): s is string {
  if (typeof s !== "string") return false;
  const m = ISO.exec(s);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (y < 2000 || y > 2100 || mo < 1 || mo > 12 || d < 1) return false;
  return d <= daysInMonth(y, mo);
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

const pad = (n: number) => String(n).padStart(2, "0");
const toIso = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const fromIso = (s: string) => {
  const m = ISO.exec(s);
  if (!m) throw new Error(`not a date: ${s}`);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
};

/** Today in the given time zone (default Budapest), as YYYY-MM-DD. */
export function todayIso(now: Date = new Date(), timeZone = "Europe/Budapest"): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  } catch {
    return toIso(now);
  }
}

export function addDays(iso: string, days: number): string {
  const d = fromIso(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toIso(d);
}

/** Calendar months later; the 31st becomes the last day of a shorter month. */
export function addMonths(iso: string, months: number): string {
  const d = fromIso(iso);
  const total = d.getUTCFullYear() * 12 + d.getUTCMonth() + months;
  const y = Math.floor(total / 12);
  const m = total - y * 12 + 1;
  return `${y}-${pad(m)}-${pad(Math.min(d.getUTCDate(), daysInMonth(y, m)))}`;
}

/** Whole days from one day to another (negative when "to" is earlier). */
export function daysBetween(from: string, to: string): number {
  return Math.round((fromIso(to).getTime() - fromIso(from).getTime()) / 864e5);
}

/** The day the reminder goes out. */
export function reminderSendDate(dueDate: string, daysBefore: number): string {
  return addDays(dueDate, -daysBefore);
}

/** later: not yet; due: goes out today (from the send day to the due day); past: the date has passed. */
export function reminderState(dueDate: string, today: string, daysBefore: number): "later" | "due" | "past" {
  if (today > dueDate) return "past";
  return today >= reminderSendDate(dueDate, daysBefore) ? "due" : "later";
}

/** The workshop's reminder settings from settings.general, with defaults and sane limits. */
export interface ReminderSettings {
  email: boolean;
  sms: boolean;
  days: Record<ReminderKind, number>;
}
export function reminderSettings(general: Record<string, unknown>): ReminderSettings {
  const flag = (key: string, d: boolean) => (general[key] === undefined || general[key] === null ? d : general[key] === true || general[key] === "true");
  const days = (key: string, d: number) => {
    const n = Number(general[key]);
    return Number.isInteger(n) && n >= 1 && n <= 120 ? n : d;
  };
  return {
    email: flag("reminderEmail", true),
    sms: flag("reminderSms", false),
    days: { inspection: days("reminderInspectionDays", REMINDER_DEFAULT_DAYS.inspection), service: days("reminderServiceDays", REMINDER_DEFAULT_DAYS.service) },
  };
}

/** Keeps known channels once, in a fixed order. */
export function cleanChannels(v: unknown): ReminderChannel[] {
  const list = Array.isArray(v) ? v : [];
  return REMINDER_CHANNELS.filter((c) => list.includes(c));
}

// ---- service interval of the type data sheet ("15 000 km / 1 év", "30.000 km or 24 months") ----

export interface ServiceInterval {
  km: number | null;
  months: number | null;
}

// unit words of the program's languages; a number stands before them
const YEAR = /^(év|éve|ev|year|yr|y|jahr|j|rok|rok[uy]|let|lat|lata|an|ani|anul|godin|god|leto|leta|рік|рок|років|р)/i;
const MONTH = /^(hónap|hó|ho\b|month|mo\b|mon|monat|mt|měsíc|mesiac|mesiac|mies|lun|mjesec|mesec|mes|місяц|міс)/i;
const EVERY_YEAR = /(?<![\p{L}\d] ?)(évente|évenként|yearly|annual|annually|jährlich|ročně|ročne|rocznie|anual|godišnje|letno|vsako leto|щороку)(?!\p{L})/iu;
const THOUSAND = /^(ezer|e\.|tis|tys|tisuć|tisoč|hiljad|тис|k|t)/i;

/** One number: "15 000", "15.000", "15,000" are fifteen thousand; "1,5" is one and a half. */
function readNumber(s: string): number {
  const t = s.replace(/[\s  ]/g, " ").trim();
  if (/^\d{1,3}([ .,]\d{3})+$/.test(t)) return Number(t.replace(/[ .,]/g, ""));
  return Number(t.replace(",", "."));
}

/**
 * Reads the km and the time of a service interval written by a mechanic or found by the AI,
 * in any of the program's languages. Null when neither is readable. Values outside a sane range
 * (km 1 000 to 100 000, 1 to 120 months) are ignored.
 */
export function parseServiceInterval(value: string | null | undefined): ServiceInterval | null {
  const text = (value ?? "").toLowerCase();
  if (!text.trim()) return null;
  let km: number | null = null;
  let months: number | null = null;
  const re = /(\d{1,3}(?:[ .,  ]\d{3})+|\d+(?:[.,]\d+)?)\s*([a-zà-žа-яёіїєґ.]+)?(?:\s*([a-zà-žа-яёіїєґ.]+))?/giu;
  for (const m of text.matchAll(re)) {
    const n = readNumber(m[1]!);
    const unit = (m[2] ?? "").replace(/\.$/, "");
    const next = (m[3] ?? "").replace(/\.$/, "");
    if (!Number.isFinite(n) || n <= 0) continue;
    if (km === null && /^km/.test(unit)) {
      if (n >= 1000 && n <= 100000) km = Math.round(n);
      else if (n >= 1 && n <= 100 && /tkm|ezer/.test(unit)) km = Math.round(n * 1000);
    } else if (km === null && THOUSAND.test(unit) && /^km/.test(next)) {
      if (n * 1000 >= 1000 && n * 1000 <= 100000) km = Math.round(n * 1000);
    } else if (km === null && /^tkm/.test(unit)) {
      if (n * 1000 >= 1000 && n * 1000 <= 100000) km = Math.round(n * 1000);
    } else if (months === null && MONTH.test(unit)) {
      if (n >= 1 && n <= 120 && Number.isInteger(n)) months = n;
    } else if (months === null && YEAR.test(unit)) {
      const mo = Math.round(n * 12);
      if (mo >= 1 && mo <= 120) months = mo;
    }
  }
  if (months === null && EVERY_YEAR.test(text)) months = 12;
  return km === null && months === null ? null : { km, months };
}

/**
 * The next service suggested from the data sheet's interval: the date when the interval gives
 * months or years (from the given day, e.g. the closing of the work order), the km when it gives
 * km and the odometer reading is known.
 */
export function suggestNextService(interval: ServiceInterval | null, from: string, odometerKm: number | null | undefined): { date: string | null; km: number | null } | null {
  if (!interval) return null;
  const date = interval.months ? addMonths(from, interval.months) : null;
  const km = interval.km && odometerKm !== null && odometerKm !== undefined && odometerKm >= 0 ? odometerKm + interval.km : null;
  return date || km ? { date, km } : null;
}
